// Cola de ventas del POS (tabla cola_ventas). payload y ticket son INMUTABLES: solo cambian los
// campos de control, y siempre a través de actualizarControl() (lista blanca + update, nunca put).
import { db } from '@/db/posDb';
import { useConexion } from '@/stores';

const CAMPOS_CONTROL = ['estado', 'intentos', 'venta_id', 'proximo_intento_en', 'ultimo_error', 'sincronizada_en'];

export const referenciaDe = (uuid) => uuid.slice(0, 8).toUpperCase();

// Reserva el correlativo local de la caja e inserta la venta en UNA transacción (cola_ventas + contadores).
// IndexedDB serializa las escrituras: dos ventas (o dos pestañas) nunca reciben el mismo número.
// Etapa 3D (D3): fecha_equipo (hora sin corregir) y desfase_aplicado_ms quedan en el registro para auditoría (no en el payload)
export async function encolarVenta({ uuid_venta, caja_id, sucursal_id, user_id, fecha_venta_cliente, fecha_equipo = null, desfase_aplicado_ms = 0, payload, ticket }) {
  const registro = await db.transaction('rw', db.cola_ventas, db.contadores, async () => {
    const cont = (await db.contadores.get(caja_id)) || { caja_id, ultimo_correlativo: 0, ventas_sesion: 0 };
    cont.ultimo_correlativo += 1;
    cont.ventas_sesion += 1; // se da por cobrada; descartarVenta() la resta si el servidor la rechaza en el acto
    await db.contadores.put(cont);
    const reg = {
      uuid_venta, caja_id, sucursal_id, user_id, fecha_venta_cliente, fecha_equipo, desfase_aplicado_ms,
      correlativo_local: cont.ultimo_correlativo,
      creada_en: new Date().toISOString(),
      payload,
      ticket: { ...ticket, correlativo_local: cont.ultimo_correlativo, referencia: referenciaDe(uuid_venta) },
      estado: 'pendiente', intentos: 0, venta_id: null, proximo_intento_en: null, ultimo_error: null, sincronizada_en: null,
    };
    await db.cola_ventas.add(reg);
    return reg;
  });
  await useConexion().refrescarContadoresCola();
  return registro;
}

export async function actualizarControl(uuid, cambios) {
  const limpio = Object.fromEntries(Object.entries(cambios).filter(([k]) => CAMPOS_CONTROL.includes(k)));
  await db.cola_ventas.update(uuid, limpio);
  await useConexion().refrescarContadoresCola();
}

// 4xx/500 del endpoint online con el cajero delante: la venta NO se cobró (no suma en ventas_sesion)
export async function descartarVenta(uuid, motivo) {
  await db.transaction('rw', db.cola_ventas, db.contadores, async () => {
    const reg = await db.cola_ventas.get(uuid);
    await db.cola_ventas.update(uuid, { estado: 'descartada', ultimo_error: motivo, sincronizada_en: new Date().toISOString() });
    const cont = await db.contadores.get(reg.caja_id);
    if (cont) await db.contadores.update(reg.caja_id, { ventas_sesion: Math.max(0, cont.ventas_sesion - 1) });
  });
  await useConexion().refrescarContadoresCola();
}

// Reintento de una venta que el servidor rechazó con el cajero delante (mismo uuid_venta, misma firma).
// Mismo registro y mismo correlativo: no se reserva número nuevo. ventas_sesion vuelve a sumar 1 porque
// descartarVenta() lo había restado. Solo se reactiva una 'descartada' (nunca aceptada por el servidor).
export async function reactivarVenta(uuid, { payload, ticket, fecha_venta_cliente, fecha_equipo = null, desfase_aplicado_ms = 0 }) {
  const registro = await db.transaction('rw', db.cola_ventas, db.contadores, async () => {
    const reg = await db.cola_ventas.get(uuid);
    if (!reg || reg.estado !== 'descartada') return null;
    const cambios = {
      payload, fecha_venta_cliente, fecha_equipo, desfase_aplicado_ms, // Etapa 3D (D3)
      ticket: { ...ticket, correlativo_local: reg.correlativo_local, referencia: referenciaDe(uuid) },
      estado: 'pendiente', intentos: 0, venta_id: null, proximo_intento_en: null, ultimo_error: null, sincronizada_en: null,
    };
    await db.cola_ventas.update(uuid, cambios);
    const cont = await db.contadores.get(reg.caja_id);
    if (cont) await db.contadores.update(reg.caja_id, { ventas_sesion: cont.ventas_sesion + 1 });
    return { ...reg, ...cambios };
  });
  await useConexion().refrescarContadoresCola();
  return registro;
}

export const obtenerVenta = (uuid) => db.cola_ventas.get(uuid);

// Para la lista local (3C): todo menos las descartadas, lo más nuevo primero
export async function listarCola() {
  const filas = await db.cola_ventas.filter((v) => v.estado !== 'descartada').toArray();
  return filas.sort((a, b) => b.creada_en.localeCompare(a.creada_en));
}
